import classNames from 'classnames';
import React from 'react';
import Carousel from './Carousel';
import CarouselCaption from './CarouselCaption';
import CarouselControls from './CarouselControls';
import CarouselSlider from './CarouselSlider';
import addTestId from '../../utils/addTestId';
import type { CarouselItem } from '../formats/ImageBlot';
import { useQuill } from '../QuillContext';
import styles from './ImageCarousel.module.scss';

const HEIGHTS = {
  xsmall: 275,
  default: 420,
  medium: 700,
  large: 900,
} as const;

export type ImageCarouselHeight = keyof typeof HEIGHTS;

export function isImageCarouselHeight(
  value: unknown,
): value is ImageCarouselHeight {
  return typeof value === 'string' && value in HEIGHTS;
}

interface ImageCarouselProps {
  images: [CarouselItem, CarouselItem, ...CarouselItem[]];
  onAddImages: (files: File[], index: number) => void;
  captionsAllowed?: boolean;
  onCaptionChange(image: CarouselItem, caption: string): void;
  height?: ImageCarouselHeight;
}

const ImageCarousel = ({
  images,
  onAddImages,
  captionsAllowed,
  onCaptionChange,
  height = 'default',
}: ImageCarouselProps) => {
  const { readOnly } = useQuill();

  return (
    <Carousel images={images} onAddImages={onAddImages}>
      <div {...addTestId('imageCarousel')} className={styles.container}>
        <div className="image-content image-container">
          <CarouselSlider
            singleMode={images.length === 1}
            dragDisabled={readOnly}
            maxHeight={HEIGHTS[height]}
          />
        </div>
        {images.length > 1 && (
          <div
            {...addTestId('imageCarouselControls')}
            className={classNames(styles.controlsContainer, styles[height])}
          >
            <CarouselControls />
          </div>
        )}
      </div>
      {captionsAllowed && (
        <CarouselCaption images={images} onCaptionChange={onCaptionChange} />
      )}
    </Carousel>
  );
};

export default ImageCarousel;
